import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import AddIcon from '@mui/icons-material/Add'
import { Link } from '@inertiajs/react'
import { SubTaskFormDialog } from '@/components/tasks/SubTaskFormDialog'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface BoardRef {
  id: number
  name: string
  slug: string
  agents?: AgentRef[]
}

export interface SubTask {
  id: number
  summary: string
  status: string
  status_color: string
  status_label: string
  assignee: AgentRef | null
  board: BoardRef
  web_url: string
  created_at: string
}

interface Props {
  subTasks: SubTask[]
  parentTaskId: number
  currentBoardSlug: string
  accessibleBoards: BoardRef[]
}

export function SubTaskList({ subTasks, parentTaskId, currentBoardSlug, accessibleBoards }: Props) {
  const [showForm, setShowForm] = useState(false)

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {subTasks.length === 0 ? 'No sub-tasks yet.' : `${subTasks.length} sub-task${subTasks.length !== 1 ? 's' : ''}`}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setShowForm(true)}
          sx={{ textTransform: 'none' }}
        >
          Add sub-task
        </Button>
      </Box>

      {subTasks.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {subTasks.map((task) => (
            <Paper
              key={task.id}
              elevation={0}
              component={Link}
              href={task.web_url}
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                textDecoration: 'none',
                color: 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                '&:hover': {
                  borderColor: 'text.secondary',
                  backgroundColor: 'grey.50',
                },
              }}
            >
              <Chip
                label={task.status_label}
                size="small"
                sx={{
                  backgroundColor: task.status_color,
                  color: '#fff',
                  fontWeight: 600,
                  height: 22,
                  fontSize: '0.7rem',
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" fontWeight={500} sx={{ flex: 1, minWidth: 0 }} noWrap>
                {task.summary}
              </Typography>
              <Chip
                label={task.board.name}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem', flexShrink: 0 }}
              />
              {task.assignee ? (
                <Chip
                  label={task.assignee.username}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
                />
              ) : (
                <Chip
                  label="Unassigned"
                  size="small"
                  variant="outlined"
                  color="default"
                  sx={{ height: 20, fontSize: '0.7rem', fontStyle: 'italic', flexShrink: 0 }}
                />
              )}
            </Paper>
          ))}
        </Box>
      )}

      <SubTaskFormDialog
        open={showForm}
        onClose={() => setShowForm(false)}
        parentTaskId={parentTaskId}
        currentBoardSlug={currentBoardSlug}
        accessibleBoards={accessibleBoards}
      />
    </Box>
  )
}
